import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
};

const fmt$ = (n) => {
  if (!n || n === 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function InsiderTrading({ session, plan }) {
  const [trades, setTrades] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    fetchTrades();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchTrades() {
    setLoading(true);
    const [{ data }, { count }] = await Promise.all([
      supabase.from('insider_trades').select('*').order('trade_date', { ascending: false }).limit(500),
      supabase.from('insider_trades').select('*', { count: 'exact', head: true }),
    ]);
    setTrades(data || []);
    setTotalCount(count || 0);
    setLoading(false);
  }

  const buyCount  = trades.filter(t => t.trade_type === 'BUY').length;
  const sellCount = trades.filter(t => t.trade_type === 'SELL').length;
  const fdaCount  = trades.filter(t => t.has_fda_event).length;

  const filtered = trades.filter(t => {
    if (filter === 'buy'  && t.trade_type !== 'BUY')  return false;
    if (filter === 'sell' && t.trade_type !== 'SELL') return false;
    if (filter === 'fda'  && !t.has_fda_event)        return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        t.ticker?.toLowerCase().includes(q) ||
        t.company_name?.toLowerCase().includes(q) ||
        t.insider_name?.toLowerCase().includes(q) ||
        t.insider_title?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          Insider Trading Signals
        </h2>
        <p style={{ color: T.textSub, fontSize: isMobile ? 14 : 16, margin: 0, lineHeight: 1.6 }}>
          SEC Form 4 filings — when biotech executives buy or sell their own stock.
          {fdaCount > 0 && <span style={{ color: T.amber, fontWeight: 700 }}> {fdaCount} trades near FDA decisions.</span>}
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total Trades', value: totalCount, color: T.blue },
          { label: 'Buy Signals',     value: buyCount,      color: T.green   },
          { label: 'Sell Signals',    value: sellCount,     color: T.red     },
          { label: 'Near FDA Events', value: fdaCount,      color: T.amber   },
        ].map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '14px 16px' : '18px 22px' }}>
            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ width: '100%', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontSize: 15 }}>⌕</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ticker, company, insider..."
            style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = T.emerald}
            onBlur={e => e.target.style.borderColor = T.border}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { id: 'all',  label: `All (${trades.length})`,     color: T.blue  },
            { id: 'buy',  label: `Buys (${buyCount})`,          color: T.green },
            { id: 'sell', label: `Sells (${sellCount})`,        color: T.red   },
            { id: 'fda',  label: `Near FDA (${fdaCount})`,      color: T.amber },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              padding: isMobile ? '8px 14px' : '10px 18px', borderRadius: 8, cursor: 'pointer',
              fontSize: isMobile ? 12 : 14, fontWeight: 600,
              border: `1px solid ${filter === f.id ? f.color : T.border}`,
              background: filter === f.id ? `${f.color}18` : 'transparent',
              color: filter === f.id ? f.color : T.textSub,
              transition: 'all 0.15s',
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 14, color: T.textMute, marginBottom: 16 }}>
        {filtered.length} results · SEC EDGAR Form 4
      </div>

      {/* Table / Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 15, marginBottom: 12 }}>Loading insider trades...</div>
          <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', background: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>No trades found</div>
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((t, i) => {
            const isBuy = t.trade_type === 'BUY';
            const tradeColor = isBuy ? T.green : T.red;
            return (
              <div key={t.id || i} style={{
                background: t.has_fda_event ? 'rgba(245,158,11,0.04)' : T.surface,
                border: `1px solid ${t.has_fda_event ? 'rgba(245,158,11,0.3)' : T.border}`,
                borderLeft: `3px solid ${t.has_fda_event ? T.amber : tradeColor}`,
                borderRadius: 12, padding: '16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 800, color: T.emerald, fontSize: 16 }}>{t.ticker}</span>
                    {t.has_fda_event && <span style={{ fontSize: 10, background: 'rgba(245,158,11,0.15)', color: T.amber, borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>FDA {t.fda_event_days}d</span>}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 100, background: isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: tradeColor }}>
                    {isBuy ? '▲ BUY' : '▼ SELL'}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600, marginBottom: 3 }}>{t.insider_name || '—'}</div>
                <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>{t.insider_title || '—'}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: tradeColor }}>{fmt$(t.total_value)}</span>
                  <span style={{ fontSize: 13, color: T.textSub }}>{fmtDateShort(t.trade_date)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 90px 1fr 1fr 100px 110px 100px', padding: '12px 20px', background: T.surfaceHi, borderBottom: `1px solid ${T.border}` }}>
            {['Ticker', 'Type', 'Insider', 'Title', 'Shares', 'Value', 'Date'].map(h => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>
          {filtered.map((t, i) => {
            const isBuy = t.trade_type === 'BUY';
            const tradeColor = isBuy ? T.green : T.red;
            return (
              <div key={t.id || i} style={{
                display: 'grid', gridTemplateColumns: '80px 90px 1fr 1fr 100px 110px 100px',
                padding: '14px 20px',
                borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : 'none',
                background: t.has_fda_event ? 'rgba(245,158,11,0.04)' : 'transparent',
                borderLeft: t.has_fda_event ? `3px solid ${T.amber}` : '3px solid transparent',
                alignItems: 'center', transition: 'background 0.12s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.has_fda_event ? 'rgba(245,158,11,0.08)' : T.surfaceHover}
                onMouseLeave={e => e.currentTarget.style.background = t.has_fda_event ? 'rgba(245,158,11,0.04)' : 'transparent'}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 800, color: T.emerald, fontSize: 15 }}>{t.ticker}</span>
                  {t.has_fda_event && <span style={{ fontSize: 10, background: 'rgba(245,158,11,0.15)', color: T.amber, borderRadius: 4, padding: '1px 6px', fontWeight: 700, width: 'fit-content' }}>FDA {t.fda_event_days}d</span>}
                </div>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 100, background: isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: tradeColor }}>
                    {isBuy ? '▲ BUY' : '▼ SELL'}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 500, paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.insider_name}>{t.insider_name || '—'}</div>
                <div style={{ fontSize: 13, color: T.textSub, paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.insider_title}>{t.insider_title || '—'}</div>
                <div style={{ fontSize: 14, color: T.textSub }}>{t.shares ? Number(t.shares).toLocaleString() : '—'}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: tradeColor }}>{fmt$(t.total_value)}</div>
                <div style={{ fontSize: 13, color: T.textSub }}>{fmtDate(t.trade_date)}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 28, padding: '14px 18px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          <strong style={{ color: T.text }}>Source:</strong> SEC EDGAR Form 4 filings. Open-market purchases and sales only. Updated weekly. Not financial advice.
        </div>
      </div>

      <style>{`@keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}